import {
  BaseAvailabilityStatus,
  type ActivityCalendar,
  type OptionProduct
} from '@linktivity/ars-neo';
import {
  areAllOptionsSoldOutForMainTimeSlot,
  areAllOptionsSoldOutOnDate,
  isSameDayLinkage
} from '@link/share/utils';
import type { CalendarDay } from './calendarRange';

/*
 * Required options and the main calendar: a day, or a time slot, where the
 * main product has stock but no required option does cannot be booked, so the
 * calendar shows it as sold out. From share's `useProductPage`, unchanged in
 * behaviour.
 */

/** Each required option's calendar, by the option's productContentId. */
export type OptionCalendars = Map<string, ActivityCalendar>;

/** What the main booking needs from the options to be judged against them. */
export interface MainBookingContext {
  optionProducts: OptionProduct[];
  optionCalendars: OptionCalendars;
  /** `coveredUntil(optionCalendars)`, worked out once. */
  coveredUntil: string;
  mainIsOpenDate: boolean;
  mainValidityExpiration: string;
  today: string;
}

/**
 * The latest date EVERY option calendar covers: the earliest of their last
 * dates. Dates after it are not judged. The minimum rather than the maximum
 * because a date missing from a calendar counts as sold out, so the shorter
 * calendars would mark dates only the longer ones reach as sold out. '' when
 * any calendar is empty, which turns the judging off.
 */
export function coveredUntil(optionCalendars: OptionCalendars): string {
  let min = '';
  for (const calendar of optionCalendars.values()) {
    let max = '';
    for (const day of calendar.dailyAvailabilities ?? []) {
      if (day.date && day.date > max) max = day.date;
    }
    if (!max) return '';
    if (!min || max < min) min = max;
  }
  return min;
}

/** Days with main stock where every required option is sold out. */
export function findOptionSoldOutDates(
  days: CalendarDay[],
  context: MainBookingContext
): Set<string> {
  const result = new Set<string>();
  if (!context.optionProducts.length || !context.coveredUntil) return result;
  for (const day of days) {
    if (!day.date || day.date > context.coveredUntil) continue;
    if (
      areAllOptionsSoldOutOnDate(
        context.optionProducts,
        context.optionCalendars,
        {
          mainTargetDate: day.date,
          mainIsOpenDate: context.mainIsOpenDate,
          mainValidityExpiration: context.mainValidityExpiration,
          today: context.today
        }
      )
    ) {
      result.add(day.date);
    }
  }
  return result;
}

/**
 * Whether time slots need judging at all: only a same-day-linked required
 * option ties the main start time to the option's own slots.
 */
export function hasSameDayLinkedOption(context: MainBookingContext): boolean {
  return (
    context.optionCalendars.size > 0 &&
    context.optionProducts.some(option => isSameDayLinkage(option.timeLinkage))
  );
}

/**
 * The day with every start-time slot that no same-day-linked option can serve
 * marked sold out. Days past the option calendars' coverage, or without slot
 * data, are returned as they are — the same object when nothing changed.
 */
export function markUnservedSlots(
  day: CalendarDay,
  context: MainBookingContext
): CalendarDay {
  if (
    !day.date ||
    day.date > context.coveredUntil ||
    !day.timeAvailabilities?.length
  ) {
    return day;
  }
  const date = day.date;
  let changed = false;
  const timeAvailabilities = day.timeAvailabilities.map(slot => {
    if (
      slot.status !== BaseAvailabilityStatus.AvailabilityStatusOk ||
      !slot.time ||
      !areAllOptionsSoldOutForMainTimeSlot(
        context.optionProducts,
        context.optionCalendars,
        {
          mainTargetDate: date,
          mainStartTime: slot.time,
          mainIsOpenDate: context.mainIsOpenDate,
          mainValidityExpiration: context.mainValidityExpiration,
          today: context.today
        }
      )
    ) {
      return slot;
    }
    changed = true;
    return { ...slot, status: BaseAvailabilityStatus.AvailabilityStatusNg };
  });
  return changed ? { ...day, timeAvailabilities } : day;
}

/**
 * The days as the calendar renders them: whole days the options sold out
 * marked sold out, and, when slots need judging, the slots of `slotDates` —
 * the dates the plans have selected, which are the only ones whose slots are
 * on screen. The other dates' slots are judged on demand (see bookability).
 * Selected-day logic keeps reading the raw days, for real units and prices.
 */
export function applyOptionAvailability(
  days: CalendarDay[],
  soldOutDates: Set<string>,
  slotDates: Set<string> | null,
  context: MainBookingContext
): CalendarDay[] {
  if (soldOutDates.size === 0 && !slotDates) return days;
  return days.map(day => {
    if (!day.date) return day;
    if (soldOutDates.has(day.date)) {
      return { ...day, status: BaseAvailabilityStatus.AvailabilityStatusNg };
    }
    return slotDates?.has(day.date) ? markUnservedSlots(day, context) : day;
  });
}
